import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Archive, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getDeadCapitalBreakdown, track } from "../lib/api";
import CountUp from "./CountUp";
import AskRailButton from "./AskRailButton";
import ExportDeadStockButton from "./ExportDeadStockButton";

/**
 * DeadCapitalStrip — compact 1-row variant of DeadCapitalHero for HomeFeed.
 *
 * The full hero lives at /inventory. This strip exists because most users
 * never navigate there on their own. Putting the bottom-line ₪ figure +
 * "fix it" CTA on the homepage means they confront frozen capital the
 * moment they log in.
 */
export default function DeadCapitalStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getDeadCapitalBreakdown()
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data?.available || data.total_value <= 0) return null;

  const oldBuckets = data.buckets.filter((b) => ["180d-1y", "1y-2y", "2y+"].includes(b.id));
  const oldValue = oldBuckets.reduce((s, b) => s + b.value, 0);
  const oldPct = (100 * oldValue) / data.total_value;

  const goToInventory = () => {
    track("dead_capital_strip_click", "/", { total: data.total_value });
    navigate("/inventory");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="relative bg-gradient-to-r from-severity-critical/[0.10] via-card to-card border border-severity-critical/30 rounded-lg p-4 mb-4 overflow-hidden group"
      data-testid="dead-capital-strip"
    >
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-severity-critical/15 blur-3xl rounded-full pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Icon + label */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 rounded-lg bg-severity-critical/15 border border-severity-critical/35 flex items-center justify-center">
            <Archive className="w-5 h-5 text-severity-critical" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-severity-critical font-mono">
              הון מוקפא במלאי
            </div>
            <button
              onClick={goToInventory}
              className="text-2xl sm:text-3xl font-mono font-semibold text-severity-critical leading-none tabular-nums hover:underline underline-offset-4 decoration-severity-critical/40"
              data-testid="dead-capital-strip-total"
            >
              ₪<CountUp value={Math.round(data.total_value)} />
            </button>
          </div>
        </div>

        {/* Story */}
        <div className="flex-1 min-w-0 text-xs text-zinc-400 leading-relaxed">
          <span className="text-foreground font-medium">{data.total_skus.toLocaleString()}</span> מק״טים שלא נמכרו 60+ ימים.{" "}
          {oldPct > 50 && (
            <span className="text-severity-critical font-medium">
              {oldPct.toFixed(0)}% מזה ({" "}
              ₪{Math.round(oldValue).toLocaleString()}) תקוע מעל חצי שנה.
            </span>
          )}
          {data.worst_offender && (
            <div className="mt-1 text-[11px] text-zinc-500 truncate">
              <span className="text-zinc-600">החמור ביותר:</span>{" "}
              <span className="text-foreground">{data.worst_offender.product}</span>{" "}
              <span className="text-severity-critical font-mono">₪{Math.round(data.worst_offender.value).toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <ExportDeadStockButton variant="ghost" testId="dead-capital-strip-export" />
          <AskRailButton
            label="איך לשחרר?"
            question={`יש לי ₪${Math.round(data.total_value).toLocaleString()} הון תקוע במלאי על ${data.total_skus} מק"טים שלא נמכרו 60+ ימים. ${oldPct > 50 ? `${oldPct.toFixed(0)}% מזה (₪${Math.round(oldValue).toLocaleString()}) תקוע מעל חצי שנה. ` : ""}תן לי תכנית פעולה ספציפית.`}
            context={{ label: "הון מוקפא", sublabel: `₪${Math.round(data.total_value).toLocaleString()}` }}
            testId="dead-capital-strip-ask"
          />
          <button
            onClick={goToInventory}
            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-foreground border border-border hover:border-zinc-600 px-2.5 py-1 rounded-md transition-all"
            data-testid="dead-capital-strip-open-inventory"
          >
            <span>פתח מלאי</span>
            <ArrowLeft className="w-3 h-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
